import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { ArrowLeft } from 'lucide-react';

const TermsOfUse: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();
    const legal = t.legal || {};
    const content = legal.termsContent || {};

    useSEO({
        title: legal.seo?.terms?.title || "Termos de Uso - NexuCapital",
        description: legal.seo?.terms?.description || "Termos e condicoes de uso da plataforma NexuCapital.",
        path: '/terms-of-use',
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        noindex: false,
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Legal', url: '/legal' },
                { name: 'Terms of Use', url: '/terms-of-use' }
            ]),
            generateWebPageSchema({
                name: legal.seo?.terms?.title || "Termos de Uso - NexuCapital",
                description: legal.seo?.terms?.description || "Termos e condicoes de uso da plataforma NexuCapital.",
                url: '/terms-of-use',
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>

            <section className="pt-24 pb-16 bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4">
                    {/* Back Link */}
                    <a
                        href="/"
                        className="inline-flex items-center text-primary hover:text-primary-dark mb-6 transition-colors"
                    >
                        <ArrowLeft size={18} className="mr-2" />
                        {t.navigation?.home || "Inicio"}
                    </a>

                    {/* Content Card */}
                    <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 md:p-12 max-w-4xl mx-auto">
                        {/* Header */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary mb-4 pb-4 border-b-2 border-secondary">
                            {legal.termsOfUse || "Termos de Uso"}
                        </h1>

                        <p className="text-gray-600 mb-8">
                            <strong>{legal.lastUpdated || "Ultima atualizacao"}:</strong> {legal.lastUpdatedDate || "01 de setembro de 2025"}
                        </p>

                        {/* Intro */}
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.intro}
                        </p>

                        {/* Section 1 - Our Services */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section1Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section1Text}
                        </p>

                        {/* Section 2 - Capacity and Eligibility */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section2Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section2Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section2Items?.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>

                        {/* Section 3 - What We Expect From You */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section3Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section3Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section3Items?.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>

                        {/* Section 4 - Use of Client Area */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section4Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section4Text1}
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section4Text2}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-4">
                            {content.section4Items?.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section4Text3}
                        </p>

                        {/* Section 5 - Intellectual Property */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section5Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section5Text}
                        </p>

                        {/* Section 6 - Service Availability */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section6Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section6Text}
                        </p>

                        {/* Section 7 - Warranties and Disclaimer */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section7Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section7Text}
                        </p>

                        {/* Section 8 - Limitation of Liability */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section8Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section8Text}
                        </p>

                        {/* Section 9 - Indemnification */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section9Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section9Text}
                        </p>

                        {/* Section 10 - Suspension or Termination */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section10Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section10Text}
                        </p>

                        {/* Section 11 - Changes to Terms */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section11Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section11Text}
                        </p>

                        {/* Section 12 - General Provisions */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section12Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section12Text}
                        </p>

                        {/* Section 13 - Applicable Law and Jurisdiction */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section13Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            {content.section13Text}
                        </p>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
};

export default TermsOfUse;
